import React, { FC, useEffect, useRef, useState } from 'react';

interface DateInputProps {
	value?: Date;
	onChange: (date: Date) => void;
}

interface DateParts {
	day: number;
	month: number;
	year: number;
}

/* In-progress editing state: a field can be momentarily empty (`''`) while the
 * user clears and retypes. Committed values are always numbers (DateParts). */
interface DraftParts {
	day: number | '';
	month: number | '';
	year: number | '';
}

const toParts = (d: Date): DateParts => ({
	day: d.getDate(),
	month: d.getMonth() + 1,
	year: d.getFullYear(),
});

const isValidDate = (p: DateParts): boolean => {
	if (p.month < 1 || p.month > 12 || p.day < 1 || p.day > 31 || p.year < 1000 || p.year > 9999) return false;
	const d = new Date(p.year, p.month - 1, p.day);
	return d.getFullYear() === p.year && d.getMonth() + 1 === p.month && d.getDate() === p.day;
};

/* Internal date input used only by PunchupDateRange — not exported from index.ts. */
export const PunchupDateRangeInput: FC<DateInputProps> = ({ value, onChange }) => {
	const [parts, setParts] = useState<DraftParts>(() => toParts(value ?? new Date()));
	const committed = useRef<DateParts>(toParts(value ?? new Date()));
	/* Tracks the current in-progress values synchronously so that the year
	 * auto-advance commit path is not affected by React's batched setState
	 * (which can leave DOM refs at the previously rendered value). */
	const pendingRef = useRef<DraftParts>(committed.current);
	/* Timestamp guard: skip the sync when the parent passes a new Date reference
	 * with the same millisecond value, so in-progress typing is not overwritten. */
	const prevValueRef = useRef<Date | undefined>(value);

	const monthRef = useRef<HTMLInputElement>(null);
	const dayRef = useRef<HTMLInputElement>(null);
	const yearRef = useRef<HTMLInputElement>(null);

	useEffect(() => {
		if (!value) return;
		if (prevValueRef.current?.getTime() === value.getTime()) {
			prevValueRef.current = value;
			return;
		}
		prevValueRef.current = value;
		const next = toParts(value);
		setParts(next);
		committed.current = next;
		pendingRef.current = next;
	}, [value]);

	const commit = (next: DateParts) => {
		committed.current = next;
		onChange(new Date(next.year, next.month - 1, next.day));
	};

	/* Read all three fields from DOM refs, validate, and commit.
	 * Used by blur and Enter key (DOM is reliable when focus leaves the group). */
	const commitFromRefs = () => {
		const month = Number(monthRef.current?.value);
		const day = Number(dayRef.current?.value);
		const year = Number(yearRef.current?.value);
		const raw = { month, day, year };
		if (isValidDate(raw)) {
			commit(raw);
			return;
		}
		const maxDay = new Date(year, month, 0).getDate();
		const clamped = { month, day: Math.min(day, maxDay || 31), year };
		if (isValidDate(clamped)) commit(clamped);
		else setParts(committed.current);
	};

	/* Read from pendingRef (not DOM) for auto-advance commits.
	 * DOM refs may still hold the previous rendered value when React batches
	 * setParts alongside a synchronous focus() call (e.g. day "20" → year focus). */
	const commitFromPending = () => {
		const { month, day, year } = pendingRef.current;
		const raw = { month: Number(month), day: Number(day), year: Number(year) };
		if (isValidDate(raw)) {
			commit(raw);
			return;
		}
		const maxDay = new Date(raw.year, raw.month, 0).getDate();
		const clamped = { ...raw, day: Math.min(raw.day, maxDay || 31) };
		if (isValidDate(clamped)) commit(clamped);
		else {
			setParts(committed.current);
			pendingRef.current = committed.current;
		}
	};

	const handleChange = (field: keyof DateParts) => (e: React.ChangeEvent<HTMLInputElement>) => {
		const raw = e.target.value;
		const numVal: number | '' = raw === '' ? '' : Number(raw);
		pendingRef.current = { ...pendingRef.current, [field]: numVal };
		setParts((p) => ({ ...p, [field]: numVal }));
		// Auto-advance focus when the field is full; commit when year completes.
		if (field === 'month' && raw.length === 2) dayRef.current?.focus();
		else if (field === 'day' && raw.length === 2) yearRef.current?.focus();
		else if (field === 'year' && raw.length === 4) commitFromPending();
	};

	const handleBlur = (e: React.FocusEvent<HTMLInputElement>) => {
		const withinGroup = [monthRef, dayRef, yearRef].some((r) => r.current === e.relatedTarget);
		if (withinGroup) return;
		commitFromRefs();
	};

	const handleKeyDown = (field: keyof DateParts) => (e: React.KeyboardEvent<HTMLInputElement>) => {
		if (e.metaKey || e.ctrlKey) return;
		if (
			!/^[0-9]$/.test(e.key) &&
			!['ArrowUp', 'ArrowDown', 'ArrowLeft', 'ArrowRight', 'Delete', 'Tab', 'Backspace', 'Enter'].includes(e.key)
		) {
			e.preventDefault();
			return;
		}

		if (e.key === 'Enter') {
			e.preventDefault();
			commitFromRefs();
			return;
		}

		if (e.key === 'ArrowUp' || e.key === 'ArrowDown') {
			e.preventDefault();
			const inc = e.key === 'ArrowUp' ? 1 : -1;
			/* An in-progress field can be empty (''); coerce to numbers so the
			 * step arithmetic operates on DateParts, not the editing DraftParts. */
			const cur: DateParts = { month: Number(parts.month), day: Number(parts.day), year: Number(parts.year) };
			const next: DateParts = { ...cur };
			if (field === 'month') {
				next.month = ((cur.month - 1 + inc + 12) % 12) + 1;
			} else if (field === 'day') {
				const maxDay = new Date(cur.year, cur.month, 0).getDate();
				next.day = ((cur.day - 1 + inc + maxDay) % maxDay) + 1;
			} else {
				next.year = Math.max(1000, Math.min(9999, cur.year + inc));
			}
			setParts(next);
			if (isValidDate(next)) commit(next);
			return;
		}

		if (e.key === 'ArrowRight') {
			const sel = e.currentTarget.selectionStart;
			const all = sel === 0 && e.currentTarget.selectionEnd === e.currentTarget.value.length;
			if (sel === e.currentTarget.value.length || all) {
				e.preventDefault();
				if (field === 'month') dayRef.current?.focus();
				if (field === 'day') yearRef.current?.focus();
			}
		} else if (e.key === 'ArrowLeft') {
			const sel = e.currentTarget.selectionStart;
			if (sel === 0) {
				e.preventDefault();
				if (field === 'day') monthRef.current?.focus();
				if (field === 'year') dayRef.current?.focus();
			}
		}
	};

	const onFocus = (e: React.FocusEvent<HTMLInputElement>) => {
		if (window.innerWidth > 1024) e.target.select();
	};

	return (
		<div className="punchupDateRangeInputField">
			<input
				ref={monthRef}
				type="text"
				maxLength={2}
				value={parts.month}
				className="punchupDateRangeInputPart"
				placeholder="M"
				onChange={handleChange('month')}
				onKeyDown={handleKeyDown('month')}
				onFocus={onFocus}
				onBlur={handleBlur}
				aria-label="Month"
			/>
			<span className="punchupDateRangeInputSlash">/</span>
			<input
				ref={dayRef}
				type="text"
				maxLength={2}
				value={parts.day}
				className="punchupDateRangeInputPart punchupDateRangeInputPartDay"
				placeholder="D"
				onChange={handleChange('day')}
				onKeyDown={handleKeyDown('day')}
				onFocus={onFocus}
				onBlur={handleBlur}
				aria-label="Day"
			/>
			<span className="punchupDateRangeInputSlash">/</span>
			<input
				ref={yearRef}
				type="text"
				maxLength={4}
				value={parts.year}
				className="punchupDateRangeInputPart punchupDateRangeInputPartYear"
				placeholder="YYYY"
				onChange={handleChange('year')}
				onKeyDown={handleKeyDown('year')}
				onFocus={onFocus}
				onBlur={handleBlur}
				aria-label="Year"
			/>
		</div>
	);
};
PunchupDateRangeInput.displayName = 'PunchupDateRangeInput';
